'use client';

import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import Link from 'next/link';
import './cyber.css';

/* All styling lives in cyber.css (scoped to .cyber-page).
   Header/Footer come from the site layout, not this file.
   Images used from public/assets/images/:
   Cybersecurity.jpg (hero, set in cyber.css), manufacturing2.jpg (story), asset-cta.jpg (final CTA).
   "From risk to resilience" cards: har card ka image path neeche `flow` array me `img` field me badlo.
   Links: `solutions` aur `industries` arrays me `href` field me badlo. Jis item ka page nahi hai uska href nahi hai. */

type IconName =
  | 'target' | 'gear' | 'users' | 'chart' | 'eye' | 'search' | 'shield'
  | 'cloud' | 'id' | 'doc' | 'alert' | 'check' | 'clock'
  | 'factory' | 'cart' | 'bank' | 'bolt' | 'heart' | 'lock';

function Icon({ name }: { name: IconName }) {
  const p: Record<IconName, ReactNode> = {
    target: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1.5" /></>,
    gear: <><circle cx="12" cy="12" r="3" /><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" /></>,
    users: <><circle cx="9" cy="8" r="3" /><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6M16 5a3 3 0 0 1 0 6M18 14c1.8.8 3 2.6 3 5" /></>,
    chart: <><path d="M4 20V4M4 20h16" /><rect x="8" y="12" width="3" height="6" /><rect x="13" y="8" width="3" height="10" /></>,
    eye: <><path d="M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></>,
    search: <><circle cx="11" cy="11" r="6" /><path d="m20 20-4.2-4.2" /></>,
    shield: <><path d="M12 3 5 6v5c0 4.5 3 8 7 10 4-2 7-5.5 7-10V6z" /><path d="m9 12 2 2 4-4" /></>,
    cloud: <><path d="M7 18a4 4 0 0 1-.5-8A5.5 5.5 0 0 1 17 8.5 4.8 4.8 0 0 1 17 18z" /></>,
    id: <><circle cx="12" cy="8" r="3.5" /><path d="M5 20c0-3.9 3.1-7 7-7s7 3.1 7 7" /></>,
    doc: <><path d="M7 3h7l5 5v13H7z" /><path d="M14 3v5h5M10 13h6M10 17h6" /></>,
    alert: <><path d="M12 3 2.5 20h19z" /><path d="M12 10v4M12 17h.01" /></>,
    check: <><circle cx="12" cy="12" r="9" /><path d="m8.5 12.5 2.5 2.5 4.5-5" /></>,
    clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
    factory: <><path d="M3 21V10l6 4v-4l6 4V6h6v15z" /><path d="M7 17h2M13 17h2M18 17h1" /></>,
    cart: <><circle cx="9" cy="20" r="1.5" /><circle cx="18" cy="20" r="1.5" /><path d="M3 4h3l2.5 11h10L21 7H7" /></>,
    bank: <><path d="M3 9 12 4l9 5H3zM5 9v9M9.5 9v9M14.5 9v9M19 9v9M3 21h18" /></>,
    bolt: <><path d="M13 3 5 13h6l-1 8 8-10h-6z" /></>,
    heart: <><path d="M12 20s-8-4.6-8-10.5A4.5 4.5 0 0 1 12 7a4.5 4.5 0 0 1 8 2.5C20 15.4 12 20 12 20z" /></>,
    lock: <><rect x="5" y="11" width="14" height="9" rx="1" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></>,
  };
  return (
    <svg className="cy-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {p[name]}
    </svg>
  );
}

/* ============================== DATA ============================== */

const cycle: { key: string; icon: IconName; label: string }[] = [
  { key: 'identify', icon: 'eye', label: 'Identify risks' },
  { key: 'prevent', icon: 'shield', label: 'Prevent threats' },
  { key: 'respond', icon: 'gear', label: 'Respond and recover' },
  { key: 'detect', icon: 'search', label: 'Detect anomalies' },
];

/* "3x" matches the customer story below */
const outcomes: { icon: IconName; value: string; label: string }[] = [
  { icon: 'shield', value: '3x', label: 'Faster threat detection' },
  { icon: 'chart', value: '50%', label: 'Reduction in security incidents' },
  { icon: 'users', value: 'Higher', label: 'Business resilience and compliance' },
];

/* img = full path from public/ folder (extension ke saath). Har card ke liye alag file daalo. */
const flow: { icon: IconName; title: string; text: string; img: string }[] = [
  {
    icon: 'alert',
    title: 'Threats',
    text: 'Evolving and increasingly complex.',
    img: '/assets/images/Data-and-Intelligence.jpg',
  },
  {
    icon: 'lock',
    title: 'Our protection',
    text: 'AI-driven detection, prevention and response.',
    img: '/assets/images/analytics.png',
  },
  {
    icon: 'check',
    title: 'Your confidence',
    text: 'A safer, more resilient business.',
    img: '/assets/images/hero-img.jpg',
  },
];

/* href = card ka link (header ke menu se). Jin areas ka alag page nahi hai unme href nahi hai. */
const solutions: { icon: IconName; title: string; text: string; href?: string }[] = [
  { icon: 'shield', title: 'Security operations', text: '24/7 monitoring, threat detection and incident response.' },
  { icon: 'cloud', title: 'Cloud security', text: 'Secure your multi-cloud and hybrid environments.', href: '/enterprise-transformation/cloud' },
  { icon: 'id', title: 'Identity & access management', text: 'Protect your people, applications and data.' },
  { icon: 'doc', title: 'Risk & compliance', text: 'Meet regulatory requirements and reduce risk.', href: '/Operate/controls-compliance' },
];

const storyStats: { icon: IconName; value: string; label: string }[] = [
  { icon: 'clock', value: '3x', label: 'Faster threat detection' },
  { icon: 'chart', value: '50%', label: 'Reduction in security incidents' },
  { icon: 'shield', value: '100%', label: 'Compliance with global standards' },
];

const industries: { icon: IconName; name: string; href: string }[] = [
  { icon: 'factory', name: 'Manufacturing', href: '/industries/manufacturing' },
  { icon: 'cart', name: 'Retail & Consumer', href: '/industries/fmcg-retail' },
  { icon: 'bank', name: 'Banking & Financial Services', href: '/industries/bfsi' },
  { icon: 'bolt', name: 'Energy & Utilities', href: '/industries/energy-utilities' },
  { icon: 'heart', name: 'Life Sciences & Healthcare', href: '/industries/pharma-lifescience' },
];

/* ===================== SCROLL REVEAL ===================== */
/* One fade-up on section content. Content stays visible if JS or IntersectionObserver is missing.
   The "js" class is added first so .rv only hides content once the observer is running. */
function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = ref.current;
    if (!root || typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('rv--in');
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    root.classList.add('js');
    root.querySelectorAll<HTMLElement>('.sec-head, .flow-step, .sol, .ind, .out-row, .story-stat').forEach((n, i) => {
      n.classList.add('rv');
      n.style.setProperty('--rv-delay', `${(i % 5) * 60}ms`);
      io.observe(n);
    });
    return () => {
      io.disconnect();
      root.classList.remove('js');
    };
  }, []);
  return ref;
}

/* ============================== PAGE ============================== */

export default function Cyber() {
  const ref = useReveal();

  return (
    <div className="cyber-page" ref={ref}>
      <main>
        {/* HERO */}
        <section className="cy-hero">
          <div className="cy-hero-bg"></div>
          <div className="cy-hero-overlay"></div>
          <div className="op-container cy-hero-inner">
            <div className="cy-hero-content">
              <h1 className="cy-hero-title">Stronger security for a brighter tomorrow.</h1>
              <p className="cy-hero-text">
                We help organisations detect, prevent and respond to cyber threats with AI-driven
                security solutions, so you can innovate with confidence.
              </p>
              <div className="cy-actions">
                <Link className="btn-primary" href="/tellusyourproblem#tell-details">Talk to Our Security Experts</Link>
                <Link className="btn-outline" href="#solutions">Explore Use Cases</Link>
              </div>
            </div>
          </div>
        </section>

        {/* SMARTER APPROACH: cycle map + outcomes */}
        <section className="op-section">
          <div className="op-container">
            <div className="approach-grid">
              <div className="sec-head approach-copy">
                <h2 className="sec-title">A smarter approach to cybersecurity.</h2>
                <p className="sec-sub">
                  We combine people, process and AI to protect your data, applications and
                  infrastructure, across cloud, on-premise and hybrid environments.
                </p>
              </div>

              <div className="cycle" role="img" aria-label="Cybersecurity cycle: identify risks, prevent threats, respond and recover, detect anomalies">
                {cycle.map((c) => (
                  <div className={`cycle-node cycle-node--${c.key}`} key={c.key}>
                    <Icon name={c.icon} />
                    <span>{c.label}</span>
                  </div>
                ))}
                <div className="cycle-core">
                  <Icon name="shield" />
                  <span>Cybersecurity</span>
                </div>
              </div>

              <div className="outcomes">
                <h3 className="outcomes-title">Business outcomes</h3>
                {outcomes.map((o) => (
                  <div className="out-row" key={o.label}>
                    <span className="out-icon"><Icon name={o.icon} /></span>
                    <div>
                      <strong>{o.value}</strong>
                      <small>{o.label}</small>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FROM RISK TO RESILIENCE: 3-step flow */}
        <section className="op-section op-section--ruled">
          <div className="op-container">
            <div className="sec-head">
              <h2 className="sec-title">From risk to resilience.</h2>
              <p className="sec-sub">
                Our AI-powered security solutions help you stay ahead of evolving threats, minimise
                risks and keep your business running without disruption.
              </p>
            </div>
            <ol className="flow">
              {flow.map((f, i) => (
                <li className="flow-step" key={f.title}>
                  <div className="flow-img">
                    <img src={f.img} alt={f.title} loading="lazy" />
                  </div>
                  <div className="flow-body">
                    <span className="flow-num">{String(i + 1).padStart(2, '0')}</span>
                    <div>
                      <h3 className="card-title">{f.title}</h3>
                      <p className="card-text">{f.text}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* KEY SOLUTION AREAS */}
        <section className="op-section op-section--ruled" id="solutions">
          <div className="op-container">
            <div className="sec-head">
              <h2 className="sec-title">Key solution areas</h2>
              <p className="sec-sub">Four areas where we help you reduce risk and respond faster.</p>
            </div>
            <div className="sol-grid">
              {solutions.map((s) => {
                const body = (
                  <>
                    <span className="sol-icon"><Icon name={s.icon} /></span>
                    <div>
                      <h3 className="card-title">{s.title}</h3>
                      <p className="card-text">{s.text}</p>
                    </div>
                  </>
                );
                /* href na ho to card plain article rahega (click nahi hoga) */
                return s.href ? (
                  <Link className="sol" key={s.title} href={s.href}>
                    {body}
                  </Link>
                ) : (
                  <article className="sol" key={s.title}>
                    {body}
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* CUSTOMER STORY */}
        <section className="op-section op-section--ruled">
          <div className="op-container">
            <div className="story">
              <div className="story-photo" role="img" aria-label="Global manufacturing facility"></div>
              <div className="story-copy">
                <p className="story-label">Customer story</p>
                <h2 className="sec-title">Strengthening security for a global manufacturer.</h2>
                <p className="sec-sub">
                  Orane helped a global manufacturing company detect and block threats 3x faster,
                  reduce security incidents by 50% and achieve stronger compliance across 20+ regions.
                </p>
              </div>
              <div className="story-stats">
                {storyStats.map((s) => (
                  <div className="story-stat" key={s.label}>
                    <Icon name={s.icon} />
                    <div>
                      <strong>{s.value}</strong>
                      <small>{s.label}</small>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* INDUSTRIES */}
        <section className="op-section op-section--ruled">
          <div className="op-container">
            <div className="sec-head">
              <h2 className="sec-title">Industries we serve</h2>
            </div>
            <div className="ind-grid">
              {industries.map((i) => (
                <Link className="ind" key={i.name} href={i.href}>
                  <Icon name={i.icon} />
                  <span>{i.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="cy-cta" id="contact">
          <div className="cy-cta-bg"></div>
          <div className="cy-cta-overlay"></div>
          <div className="op-container cy-cta-inner">
            <h2 className="cy-cta-title">Let&rsquo;s build a safer, more resilient business.</h2>
            <p className="cy-cta-text">
              Tell us where your biggest security risk is. We&rsquo;ll help you close the gap.
            </p>
            <Link className="btn-primary" href="/tellusyourproblem#tell-details">Talk to Our Experts</Link>
          </div>
        </section>
      </main>
    </div>
  );
}